// EN/SV dictionary. t(key) resolves a dotted path and can return strings, arrays or objects.
export const dict = {
  en: {
    nav: { how: "How It Works", organisers: "For Organisers", talents: "For Talents", pricing: "Pricing", spotlight: "Spotlight", signin: "Sign in", book: "Book talent now", dashboard: "Dashboard" },
    rider: { label: "Technical rider / Requirements", heading: "Technical rider" },
    hero: {
      title: "Tell us about your event.",
      titleAccent: "We find the talent.",
      subtitle: "Write a sentence or pick a category. Talents who fit your style, budget and date get in touch with priced offers.",
      post: "Book now", join: "Join as talent",
      panelTitle: "What are you celebrating?",
      panelHint: "Pick one, or describe it in your own words.",
      freeText: "Describe the event...",
      example: "Example: Wedding in Stockholm 14 Sep, 150 guests, budget 15 000",
      trust1: "Free to post", trust2: "No card details", trust3: "At most five talents can respond",
    },
    landing: {
      compare: {
        heading: "The old way vs the Talentsio way",
        oldTitle: "The old way",
        oldItems: [
          "Endless scrolling on Instagram and TikTok",
          "Sliding into DMs just to ask \"what are your rates?\"",
          "Chasing quotes across Facebook groups and email threads",
          "Choice paralysis from hundreds of look-alike profiles",
          "No idea who is available or what anything actually costs",
        ],
        newTitle: "The Talentsio way",
        newItems: [
          "One & done: post your request a single time",
          "Priced offers upfront, no more \"DM for rates\"",
          "Curated pitches from qualified, verified talents",
          "Compare real offers side by side in one place",
          "Book faster while keeping full visibility",
        ],
      },
      spotlight: {
        eyebrow: "How Talentsio works",
        heading: "Everything you need to book with confidence",
        features: [
          { title: "Priced offers", text: "Talents pitch you with a clear flat fee or hourly rate, a personal message, and portfolio links, so you compare on facts, not guesswork." },
          { title: "Smart matching", text: "Your request reaches the right verified talents for your category, date and city, so only qualified professionals apply." },
          { title: "Integrated messaging", text: "Chat with talents in one place, agree the details, and keep the whole conversation organised from offer to booking." },
        ],
        offerCard: { name: "Live jazz trio", role: "Live music", price: "12 000 SEK", note: "incl. VAT", pitch: "We'd love to play your gala. 3-piece set, 2x45 min, own PA included.", cta: "View offer" },
      },
    },
    stats: {
      items: [
        { value: "2 min", label: "To send a request" },
        { value: "Max 5", label: "Talents who can reply" },
        { value: "0 kr", label: "To post and compare offers" },
      ],
    },
    why: {
      heading: "Why organisers choose Talentsio",
      cards: [
        { title: "Free for organisers", text: "Posting a request and receiving offers costs you nothing. Talents pay a small unlock fee to respond. You just choose the best fit." },
        { title: "100% verified talent", text: "We manually review every single talent profile before they go live. No ghost accounts, no hobbyists. Only working professionals." },
        { title: "Instant one-tap approval", text: "See priced offers arrive straight in your inbox and pick the one that fits. Approve with a single tap." },
      ],
    },
    offer: {
      eyebrow: "THE OFFER SYSTEM", heading: "Talents compete for your event.",
      paragraphs: [
        "Traditional agencies show you one option. Talentsio flips the model entirely.",
        "Post one free request and watch verified talents send you their best offer, complete with pricing, a personal pitch, and full availability. Counter offer if you want. Accept when it feels right.",
      ],
      cta: "Post a free request", caption: "Illustrative example of competing offers",
      offers: [
        { name: "DJ", role: "DJ / Electronic", price: "8 500 SEK", status: "Offer sent" },
        { name: "Live jazz trio", role: "Live music", price: "12 000 SEK", status: "Offer sent" },
        { name: "Nova Live Band", role: "Cover band", price: "9 500 SEK", status: "Offer sent" },
        { name: "Sarah K.", role: "Host / MC", price: "6 000 SEK", status: "Accepted" },
      ],
    },
    cta: {
      eyebrow: "GET STARTED TODAY", titlePlain: "The right talent for your event is ", titleAccent: "waiting.",
      subtitle: "Post a free request in 2 minutes. No account needed.",
      primary: "Post a free request", secondary: "Apply as talent",
      note: "Free to post, fixed final price, verified talent only",
    },
    footer: {
      tagline: "Book the right talent for any event.",
      platform: "Platform", company: "Company", legal: "Legal",
      links: { how: "How It Works", organisers: "For Organisers", talents: "For Talents", pricing: "Pricing", faq: "FAQ", about: "About", contact: "Contact", status: "Check Booking Status", privacy: "Privacy Policy", terms: "Terms of Service", cookies: "Cookie Settings" },
      bookTitle: "Book talent",
      copyright: "© 2026 Talentsio. All rights reserved.", location: "Sweden",
    },
    cookie: {
      notice: "We use cookies to improve your experience and analyse traffic.",
      settings: "Settings", reject: "Reject", accept_all: "Accept all",
      settings_title: "Cookie settings",
      manage: "Manage which cookies we can use. Necessary cookies are always on.",
      necessary: "Necessary", necessary_hint: "Required for the site to function. Always active.",
      always_on: "Always on",
      analytics: "Analytics", analytics_hint: "Help us understand how the site is used.",
      marketing: "Marketing", marketing_hint: "Used to personalise offers and ads.",
      reject_all: "Reject all", save: "Save preferences",
    },
    booking: {
      title: "Post a free request", subtitle: "Tell us about your event. Under 2 minutes. No account needed.",
      email: "Your email", eventTitle: "Event title", category: "Talent category", date: "Event date", city: "Event city",
      phone: "Phone (optional)", budgetMin: "Budget min (SEK)", budgetMax: "Budget max (SEK)", guests: "Guest count",
      description: "Describe your event", submit: "Post request", eventType: "Event type",
      attachments: "Attachments (optional)", attachmentsHint: "Images or PDFs, max 10 MB each.",
      successTitle: "Request posted!", successText: "Verified talents can now send you offers. Save your booking code to track status.",
      yourCode: "Your booking code", reference: "Reference", track: "Track status", home: "Home",
      note: "Free to post, fixed final price, verified talent only",
    },
    status: { title: "Check booking status", placeholder: "Enter your booking code (TSO-XXXX-XXX)", check: "Check status", notFound: "No request found", city: "City", date: "Date", verified: "Email verified", offers: "Offers received", yes: "Yes", no: "No" },
    common: { signin: "Sign in", signup: "Create account", email: "Email", password: "Password", name: "Name", loading: "Loading...", back: "Back to home", getInTouch: "Get in touch" },
    pages: {
      how: {
        eyebrow: "How it works", title: "Simple for everyone.",
        subtitle: "Whether you are booking talent or getting booked, Talentsio keeps every step clear, fast and fair.",
        tabs: ["For organisers", "For talents"],
        organisers: [
          { step: "01", title: "Describe your event", text: "Fill in five fields: event type, date, city, talent category and budget. Under 2 minutes." },
          { step: "02", title: "Receive competing offers", text: "Verified talents respond with full priced offers including their pitch, requirements and availability." },
          { step: "03", title: "Confirm your act", text: "Accept the offer that fits. Counter offer to negotiate. Once confirmed, everything stays in one place." },
        ],
        talents: [
          { step: "01", title: "Create your profile", text: "Build a verified profile with your media, genres and pricing. Our team reviews it so organisers trust you." },
          { step: "02", title: "Get matched requests", text: "Receive real booking requests with a date and a budget, straight to your inbox." },
          { step: "03", title: "Send your offer", text: "Reply with your price and a personal pitch. Transparent 10% platform fee shown in every offer." },
        ],
        ctaOrg: "Post a free request", ctaTal: "Join as a talent",
      },
      org: {
        eyebrow: "For organisers", title: "Book the right talent, the easy way.",
        subtitle: "Post one free request and let verified talents compete for your event. Fixed final price, no agency markup.",
        benefits: [
          { title: "Free for organisers", text: "Posting a request and receiving offers costs you nothing. Talents pay a small unlock fee to respond, you just choose the best fit." },
          { title: "100% verified talent", text: "Every profile is manually reviewed before it goes live. No ghost accounts, no hobbyists, only working professionals." },
          { title: "Instant one-tap approval", text: "See priced offers arrive straight in your inbox and pick the one that fits. Approve with a single tap." },
          { title: "Compare real offers", text: "See up to five priced offers side by side, each with a personal pitch, requirements and availability." },
          { title: "Talk directly", text: "Message the talents who applied to your gig, counter offer, and agree the details in one place." },
          { title: "Confirmed and clear", text: "Accept an offer to confirm the booking. Contact details unlock and everything stays organised." },
        ],
        ctaTitle: "Ready to find your talent?", ctaText: "It takes under two minutes and there is no account needed to start.", cta: "Post a free request",
      },
      tal: {
        eyebrow: "For talents", title: "Get found by organisers ready to book.",
        subtitle: "Create a free profile, receive matched requests, and send offers. Keep your whole fee.",
        points: [
          { title: "Real booking requests", text: "Get gigs with a date and a budget sent to you. Only answer the ones that fit your calendar." },
          { title: "Transparent platform fee", text: "You keep your fee, minus a transparent 10% platform fee that is always shown in every offer. No hidden markups." },
          { title: "Build your reputation", text: "Collect reviews from real clients and grow a profile that gets you booked again and again." },
          { title: "Verified and trusted", text: "A reviewed, verified badge tells organisers you are the real deal, so they book with confidence." },
          { title: "Respond in seconds", text: "Send a priced offer with a personal pitch straight from your phone, wherever you are." },
          { title: "Grow on your terms", text: "Set your own pricing, styles and availability. You are always in control of your bookings." },
        ],
        ctaTitle: "Start getting booked.", ctaText: "Create your free profile today and start receiving matched requests.", cta: "Create your free profile",
      },
      about: {
        eyebrow: "About us", title: "Booking talent, done right.",
        subtitle: "Talentsio is a Swedish talent marketplace connecting event organisers with verified performers, fairly and directly.",
        paragraphs: [
          "We started Talentsio because booking great talent was too slow, too opaque and far too expensive. Agencies would show you a single option and take a large commission, while brilliant local performers stayed invisible.",
          "Our model flips that. Organisers post one free request and verified talents compete with real, priced offers. Fixed final price for customers, transparent 10% platform fee, and full visibility from the first message to the confirmed booking.",
          "Today we help people across Sweden book DJs, live bands, hosts, comedians and more for events of every size, and we are just getting started.",
        ],
        cards: [
          { title: "hello@talentsio.com", text: "The team behind Talentsio" },
          { title: "Made in Sweden", text: "Serving events nationwide" },
          { title: "Fair by design", text: "Transparent 10% platform fee, always shown in every offer" },
        ],
      },
      contact: {
        eyebrow: "Contact", title: "We would love to hear from you.",
        subtitle: "Questions, feedback or press. Send us a message and we will reply as soon as we can.",
        email: "Email", office: "Office", name: "Your name", emailField: "Email", subject: "Subject", message: "Your message", send: "Send message",
        success: "Thanks! We will get back to you shortly.",
        error: "Something went wrong. Please try again or email us directly.",
      },
      faq: {
        eyebrow: "FAQ", title: "Frequently asked questions", subtitle: "Everything you need to know about booking and getting booked on Talentsio.",
        items: [
          { q: "Is it really free for organisers?", a: "Yes. Posting a request and receiving offers is completely free. Talents pay a small credit to respond to your gig, so you never pay a commission." },
          { q: "How fast will I get offers?", a: "Verified talents reply with priced offers straight to your inbox. You can approve with a single tap." },
          { q: "Are the talents verified?", a: "Every talent profile is manually reviewed and verified before it goes live. Only working professionals appear on Talentsio." },
          { q: "How many offers can I receive?", a: "Each request can receive up to five offers, so you always have a clear set of options to compare without being overwhelmed." },
          { q: "When do I see contact details?", a: "Contact details are shared once you accept an offer and confirm the booking. This keeps everyone's information private until there is a real deal." },
          { q: "Do I need an account to post a request?", a: "No. You can post a request in under two minutes without an account, and track it with your booking code." },
          { q: "How do credits work for talents?", a: "Talents buy credit packs and spend one credit to unlock a gig they want to respond to. There is no commission on your fee." },
          { q: "Can I leave a review?", a: "Yes. After a confirmed booking, organisers can leave a rating and review, which appears on the talent's profile." },
        ],
      },
    },
    invoice: {
      title: "Invoice settings",
      lead: "These details appear on every invoice you send through Talentsio. Fill in what you want shown at the top and bottom of your invoice.",
      groups: {
        logo: "Logo",
        sender: "Sender",
        contact: "Contact",
        payment: "Payment",
        terms: "Terms",
        tax: "Tax",
        behaviour: "Behaviour",
        text: "Text",
      },
      fields: {
        logo: "Landscape logo",
        logoHint: "PNG or JPG. Around 600 by 180 pixels looks best. Without a logo, your artist name is shown instead.",
        artistName: "Artist name",
        companyName: "Company name",
        orgNumber: "Company registration number",
        vatNumber: "VAT number",
        address: "Street address",
        postcode: "Postcode",
        city: "City",
        email: "Email",
        phone: "Phone",
        website: "Website",
        bankgiro: "Bankgiro",
        plusgiro: "Plusgiro",
        swish: "Swish",
        iban: "IBAN",
        paymentTermsDays: "Payment terms (days)",
        lateInterestPct: "Late interest (%)",
        nextInvoiceNumber: "Next invoice number",
        hasFTax: "Holds F-tax",
        vatRegistered: "VAT registered",
        vatRegisteredHint: "When this is off, every VAT rate on every invoice is locked to zero.",
        autoReminder: "Send automatic reminder",
        copyToSelf: "Send a copy to me",
        defaultText: "Default invoice text",
        referenceText: "Reference",
      },
      preview: {
        heading: "How your invoice will look",
        invoiceNo: "Invoice number",
        invoiceDate: "Invoice date",
        dueDate: "Due date",
        billTo: "Bill to",
        description: "Description",
        qty: "Qty",
        unitPrice: "Unit price",
        vat: "VAT",
        amount: "Amount",
        sampleLine: "Sample gig",
        subtotal: "Subtotal",
        vatTotal: "VAT",
        total: "Total due",
        noVat: "No VAT",
        payTo: "Pay to",
        talentsioFee: "Talentsio fee",
        poweredBy: "Powered by Talentsio",
      },
      actions: { save: "Save", saving: "Saving", saved: "Saved", cancel: "Cancel", upload: "Upload" },
      errors: {
        vatBlocked: "VAT registration is required for VAT rates above zero. Turn on VAT registration and try again.",
        generic: "Could not save. Please try again.",
      },
    },
    addons: {
      title: "My add-ons",
      lead: "One list for everything you sell on top of your act. Used later in offers, on your public profile and on invoices, so keep it clean.",
      groups: {
        equipment: "Equipment",
        service: "Services",
      },
      empty: {
        equipment: "No equipment add-ons yet. Add your first one below.",
        service: "No service add-ons yet. Add your first one below.",
      },
      badges: {
        showOnProfile: "Shown on profile",
        allowQuantity: "Quantity selectable",
        onInvoice: "On invoice",
        inactive: "Hidden",
        vatLocked: "VAT locked at 0",
      },
      units: {
        piece: "per piece",
        evening: "per evening",
        hour: "per hour",
        km: "per km",
        occasion: "per occasion",
      },
      vatReasons: {
        exempt_performance: "Performance exempt from VAT",
        not_registered: "Not VAT-registered",
        standard: "Standard rate",
        reduced_admission: "Reduced (admission / rental)",
        reduced_food: "Reduced (food and drink)",
      },
      form: {
        heading: "New add-on",
        editHeading: "Edit add-on",
        name: "Name",
        namePlaceholder: "E.g. Extra performance hour",
        category: "Category",
        price: "Price (SEK)",
        pricePlaceholder: "E.g. 1500",
        unit: "Unit",
        vat: "VAT",
        vatReason: "VAT reason",
        terms: "Terms and conditions",
        termsHint: "Max 300 characters. Shown to organisers next to the price.",
        allowQuantity: "Organiser can choose quantity",
        showOnProfile: "Show on my public profile",
        usableOnInvoice: "Can be added to invoices",
        active: "Active",
      },
      actions: {
        add: "Add add-on",
        save: "Save changes",
        cancel: "Cancel",
        edit: "Edit",
        hide: "Hide",
        show: "Show",
        delete: "Remove",
        moveUp: "Move up",
        moveDown: "Move down",
      },
      confirm: {
        deleteTitle: "Remove this add-on?",
        deleteBody: "The row stays in your history so existing offers keep working, but new offers, profile and invoices will no longer see it.",
        deleteCta: "Yes, remove",
      },
      toasts: {
        saved: "Add-on saved",
        deleted: "Add-on removed",
        reordered: "Order updated",
      },
      errors: {
        vatBlocked: "You are not VAT-registered, so VAT must be zero. Turn on VAT registration in Invoice settings first.",
        generic: "Could not save. Please try again.",
      },
    },
    publicProfile: {
      addons: {
        heading: "Add-ons and extras",
        equipment: "Equipment",
        service: "Services",
        empty: "This talent hasn't listed any add-ons yet.",
        note: "Prices below are indicative. The exact terms are set in the offer.",
      },
    },
  },
  sv: {
    nav: { how: "Så fungerar det", organisers: "För arrangörer", talents: "För talanger", pricing: "Priser", spotlight: "Spotlight", signin: "Logga in", book: "Boka talang nu", dashboard: "Kontrollpanel" },
    rider: { label: "Teknisk rider / Krav", heading: "Tekniska krav" },
    hero: {
      title: "Berätta om ert event.",
      titleAccent: "Vi hittar talangen.",
      subtitle: "Skriv en mening eller välj kategori. Talanger som passar er stil, budget och datum hör av sig med offerter som har pris.",
      post: "Boka nu", join: "Gå med som talang",
      panelTitle: "Vad ska ni fira?",
      panelHint: "Välj ett, eller beskriv med egna ord.",
      freeText: "Beskriv eventet...",
      example: "Ex: Bröllop i Stockholm 14 sep, 150 gäster, budget 15 000",
      trust1: "Gratis förfrågan", trust2: "Ingen kortuppgift", trust3: "Högst fem talanger kan svara",
    },
    landing: {
      compare: {
        heading: "Det gamla sättet vs Talentsio-sättet",
        oldTitle: "Det gamla sättet",
        oldItems: [
          "Oändligt scrollande på Instagram och TikTok",
          "DM:a bara för att fråga \"vad tar ni betalt?\"",
          "Jaga offerter i Facebook-grupper och mejltrådar",
          "Beslutsångest bland hundratals likadana profiler",
          "Ingen aning om vem som är ledig eller vad något kostar",
        ],
        newTitle: "Talentsio-sättet",
        newItems: [
          "En och gjort: skicka din förfrågan en enda gång",
          "Bud med pris direkt, aldrig mer \"DM för pris\"",
          "Utvalda pitchar från kvalificerade, verifierade talanger",
          "Jämför riktiga bud sida vid sida på ett ställe",
          "Boka snabbare med full insyn",
        ],
      },
      spotlight: {
        eyebrow: "Så fungerar Talentsio",
        heading: "Allt du behöver för att boka tryggt",
        features: [
          { title: "Bud med pris", text: "Talanger pitchar dig med tydligt fast pris eller timpris, ett personligt meddelande och portföljlänkar, så att du jämför på fakta." },
          { title: "Smart matchning", text: "Din förfrågan når rätt verifierade talanger för din kategori, ditt datum och din stad, så att endast kvalificerade proffs svarar." },
          { title: "Integrerade meddelanden", text: "Chatta med talanger på ett ställe, kom överens om detaljerna och håll hela konversationen samlad från bud till bokning." },
        ],
        offerCard: { name: "Live jazz trio", role: "Livemusik", price: "12 000 SEK", note: "inkl. moms", pitch: "Vi spelar gärna på er gala. Trio, 2x45 min, egen PA ingår.", cta: "Visa bud" },
      },
    },
    stats: {
      items: [
        { value: "2 min", label: "Att skicka en förfrågan" },
        { value: "Max 5", label: "Talanger som kan svara" },
        { value: "0 kr", label: "Att posta och jämföra offerter" },
      ],
    },
    why: {
      heading: "Därför väljer arrangörer Talentsio",
      cards: [
        { title: "Gratis för arrangörer", text: "Att skicka en förfrågan och ta emot bud kostar ingenting. Någonsin. Talanger betalar en liten kreditavgift för att svara. Du väljer bara den som passar bäst." },
        { title: "100% verifierade talanger", text: "Vi granskar varje talangprofil manuellt innan den publiceras. Inga spökkonton, inga hobbyutövare. Bara yrkesverksamma proffs." },
        { title: "Matchning inom 48 tim", text: "De flesta förfrågningar får sitt första bud inom några timmar. Boka snabbare än någon agentur, med full insyn." },
      ],
    },
    offer: {
      eyebrow: "BUDSYSTEMET", heading: "Talanger tävlar om ditt event.",
      paragraphs: [
        "Traditionella agenturer visar dig ett alternativ till 20 till 30% provision. Talentsio vänder helt på modellen.",
        "Skicka en gratis förfrågan och se verifierade talanger skicka sina bästa bud, med pris, personlig pitch och full tillgänglighet. Lägg ett motbud om du vill. Acceptera när det känns rätt.",
      ],
      cta: "Skicka en gratis förfrågan", caption: "Illustrativt exempel på konkurrerande bud",
      offers: [
        { name: "DJ", role: "DJ / Elektroniskt", price: "8 500 SEK", status: "Bud skickat" },
        { name: "Live jazz trio", role: "Livemusik", price: "12 000 SEK", status: "Bud skickat" },
        { name: "Nova Live Band", role: "Coverband", price: "9 500 SEK", status: "Bud skickat" },
        { name: "Sarah K.", role: "Konferencier", price: "6 000 SEK", status: "Accepterat" },
      ],
    },
    cta: {
      eyebrow: "KOM IGÅNG IDAG", titlePlain: "Rätt talang för ditt event ", titleAccent: "väntar.",
      subtitle: "Skicka en gratis förfrågan på 2 minuter. Inget konto behövs.",
      primary: "Skicka en gratis förfrågan", secondary: "Ansök som talang",
      note: "Gratis att skicka, ingen provision, endast verifierade talanger",
    },
    footer: {
      tagline: "Boka rätt talang för alla event.",
      platform: "Plattform", company: "Företag", legal: "Juridik",
      links: { how: "Så fungerar det", organisers: "För arrangörer", talents: "För talanger", pricing: "Priser", faq: "Vanliga frågor", about: "Om oss", contact: "Kontakt", status: "Kontrollera bokningsstatus", privacy: "Integritetspolicy", terms: "Användarvillkor", cookies: "Cookie-inställningar" },
      bookTitle: "Boka talang",
      copyright: "© 2026 Talentsio. Alla rättigheter förbehållna.", location: "Sverige",
    },
    cookie: {
      notice: "Vi använder cookies för att förbättra din upplevelse och analysera trafik.",
      settings: "Inställningar", reject: "Avvisa", accept_all: "Acceptera alla",
      settings_title: "Cookie-inställningar",
      manage: "Hantera vilka cookies vi får använda. Nödvändiga cookies är alltid på.",
      necessary: "Nödvändiga", necessary_hint: "Krävs för att webbplatsen ska fungera. Alltid aktiva.",
      always_on: "Alltid på",
      analytics: "Analys", analytics_hint: "Hjälper oss förstå hur webbplatsen används.",
      marketing: "Marknadsföring", marketing_hint: "Används för att anpassa erbjudanden och annonser.",
      reject_all: "Avvisa alla", save: "Spara inställningar",
    },
    booking: {
      title: "Skicka en gratis förfrågan", subtitle: "Berätta om ditt event. Under 2 minuter. Inget konto behövs.",
      email: "Din e-post", eventTitle: "Eventets titel", category: "Talangkategori", date: "Eventdatum", city: "Stad",
      phone: "Telefon (valfritt)", budgetMin: "Budget min (SEK)", budgetMax: "Budget max (SEK)", guests: "Antal gäster",
      description: "Beskriv ditt event", submit: "Skicka förfrågan", eventType: "Typ av event",
      attachments: "Bilagor (valfritt)", attachmentsHint: "Bilder eller PDF, max 10 MB styck.",
      successTitle: "Förfrågan skickad!", successText: "Verifierade talanger kan nu skicka bud till dig. Spara din bokningskod för att följa status.",
      yourCode: "Din bokningskod", reference: "Referens", track: "Följ status", home: "Hem",
      note: "Gratis att skicka, ingen provision, endast verifierade talanger",
    },
    status: { title: "Kontrollera bokningsstatus", placeholder: "Ange din bokningskod (TSO-XXXX-XXX)", check: "Kontrollera status", notFound: "Ingen förfrågan hittades", city: "Stad", date: "Datum", verified: "E-post verifierad", offers: "Mottagna bud", yes: "Ja", no: "Nej" },
    common: { signin: "Logga in", signup: "Skapa konto", email: "E-post", password: "Lösenord", name: "Namn", loading: "Laddar...", back: "Tillbaka till start", getInTouch: "Kontakta oss" },
    pages: {
      how: {
        eyebrow: "Så fungerar det", title: "Enkelt för alla.",
        subtitle: "Oavsett om du bokar talang eller blir bokad håller Talentsio varje steg tydligt, snabbt och rättvist.",
        tabs: ["För arrangörer", "För talanger"],
        organisers: [
          { step: "01", title: "Beskriv ditt event", text: "Fyll i fem fält: typ av event, datum, stad, talangkategori och budget. Under 2 minuter." },
          { step: "02", title: "Ta emot konkurrerande bud", text: "Verifierade talanger svarar med kompletta prissatta bud inklusive pitch, krav och tillgänglighet." },
          { step: "03", title: "Bekräfta din akt", text: "Acceptera budet som passar. Lägg motbud för att förhandla. När det är bekräftat samlas allt på ett ställe." },
        ],
        talents: [
          { step: "01", title: "Skapa din profil", text: "Bygg en verifierad profil med media, genrer och priser. Vårt team granskar den så arrangörer litar på dig." },
          { step: "02", title: "Få matchade förfrågningar", text: "Ta emot riktiga bokningsförfrågningar med datum och budget, direkt i inkorgen." },
          { step: "03", title: "Skicka ditt bud", text: "Svara med ditt pris och en personlig pitch. Behåll 100% av ditt arvode, ingen provision, någonsin." },
        ],
        ctaOrg: "Skicka en gratis förfrågan", ctaTal: "Gå med som talang",
      },
      org: {
        eyebrow: "För arrangörer", title: "Boka rätt talang, på det enkla sättet.",
        subtitle: "Skicka en gratis förfrågan och låt verifierade talanger tävla om ditt event. Fast slutpris, inget agenturpåslag.",
        benefits: [
          { title: "Gratis för arrangörer", text: "Att skicka en förfrågan och ta emot bud kostar ingenting. Talanger betalar en liten kreditavgift för att svara, du väljer bara den bästa." },
          { title: "100% verifierade talanger", text: "Varje profil granskas manuellt innan den publiceras. Inga spökkonton, inga hobbyutövare, bara yrkesverksamma proffs." },
          { title: "Direktgodkännande med ett klick", text: "Prissatta bud landar direkt i din inkorg och du väljer det som passar. Godkänn med ett enda klick." },
          { title: "Jämför riktiga bud", text: "Se upp till fem prissatta bud sida vid sida, var och en med personlig pitch, krav och tillgänglighet." },
          { title: "Prata direkt", text: "Meddela talangerna som ansökt till ditt uppdrag, lägg motbud och kom överens om detaljerna på ett ställe." },
          { title: "Bekräftat och tydligt", text: "Acceptera ett bud för att bekräfta bokningen. Kontaktuppgifter låses upp och allt hålls organiserat." },
        ],
        ctaTitle: "Redo att hitta din talang?", ctaText: "Det tar under två minuter och inget konto behövs för att börja.", cta: "Skicka en gratis förfrågan",
      },
      tal: {
        eyebrow: "För talanger", title: "Bli hittad av arrangörer redo att boka.",
        subtitle: "Skapa en gratis profil, ta emot matchade förfrågningar och skicka bud. Behåll hela ditt arvode.",
        points: [
          { title: "Riktiga bokningsförfrågningar", text: "Få uppdrag med datum och budget skickade till dig. Svara bara på dem som passar din kalender." },
          { title: "Transparent plattformsavgift", text: "Du behåller ditt arvode, minus en transparent 10% plattformsavgift som alltid syns i varje bud. Inga dolda påslag." },
          { title: "Bygg ditt rykte", text: "Samla recensioner från riktiga kunder och bygg en profil som blir bokad om och om igen." },
          { title: "Verifierad och betrodd", text: "En granskad, verifierad märkning visar arrangörer att du är på riktigt, så de bokar med förtroende." },
          { title: "Svara på sekunder", text: "Skicka ett prissatt bud med en personlig pitch direkt från mobilen, var du än är." },
          { title: "Väx på dina villkor", text: "Sätt dina egna priser, stilar och tillgänglighet. Du har alltid kontroll över dina bokningar." },
        ],
        ctaTitle: "Börja bli bokad.", ctaText: "Skapa din gratis profil idag och börja ta emot matchade förfrågningar.", cta: "Skapa din gratis profil",
      },
      about: {
        eyebrow: "Om oss", title: "Att boka talang, gjort rätt.",
        subtitle: "Talentsio är en svensk talangmarknadsplats som kopplar samman arrangörer med verifierade artister, rättvist och direkt.",
        paragraphs: [
          "Vi startade Talentsio eftersom det var för långsamt, för otydligt och alldeles för dyrt att boka bra talang. Agenturer visade ett enda alternativ och tog en stor provision, medan lokala stjärnor förblev osynliga.",
          "Vår modell vänder på det. Arrangörer skickar en gratis förfrågan och verifierade talanger tävlar med riktiga, prissatta bud. Fast slutpris för arrangörer, transparent 10% plattformsavgift, inga spökkonton och full insyn från första meddelandet till bekräftad bokning.",
          "Idag hjälper vi människor över hela Sverige att boka DJ:s, liveband, konferencierer, komiker och mer för event av alla storlekar, och vi har bara börjat.",
        ],
        cards: [
          { title: "hello@talentsio.com", text: "Teamet bakom Talentsio" },
          { title: "Gjort i Sverige", text: "Vi hjälper event i hela landet" },
          { title: "Rättvist från grunden", text: "0% provision för arrangörer" },
        ],
      },
      contact: {
        eyebrow: "Kontakt", title: "Vi vill gärna höra från dig.",
        subtitle: "Frågor, feedback eller press. Skicka ett meddelande så svarar vi så snart vi kan.",
        email: "E-post", office: "Kontor", name: "Ditt namn", emailField: "E-post", subject: "Ämne", message: "Ditt meddelande", send: "Skicka meddelande",
        success: "Tack! Vi återkommer till dig inom kort.",
        error: "Något gick fel. Försök igen eller mejla oss direkt.",
      },
      faq: {
        eyebrow: "Vanliga frågor", title: "Vanliga frågor", subtitle: "Allt du behöver veta om att boka och bli bokad på Talentsio.",
        items: [
          { q: "Är det verkligen gratis för arrangörer?", a: "Ja. Att skicka en förfrågan och ta emot bud är helt gratis. Talanger betalar en liten kredit för att svara på ditt uppdrag, så du betalar aldrig någon provision." },
          { q: "Hur snabbt får jag bud?", a: "Verifierade talanger svarar med prissatta bud direkt i din inkorg. Du kan godkänna med ett klick." },
          { q: "Är talangerna verifierade?", a: "Varje talangprofil granskas manuellt och verifieras innan den publiceras. Endast yrkesverksamma proffs finns på Talentsio." },
          { q: "Hur många bud kan jag få?", a: "Varje förfrågan kan få upp till fem bud, så du har alltid en tydlig uppsättning alternativ att jämföra utan att bli överväldigad." },
          { q: "När ser jag kontaktuppgifter?", a: "Kontaktuppgifter delas när du accepterar ett bud och bekräftar bokningen. Det håller allas information privat tills det finns en riktig affär." },
          { q: "Behöver jag ett konto för att skicka en förfrågan?", a: "Nej. Du kan skicka en förfrågan på under två minuter utan konto, och följa den med din bokningskod." },
          { q: "Hur fungerar krediter för talanger?", a: "Talanger köper kreditpaket och använder en kredit för att låsa upp ett uppdrag de vill svara på. Det finns ingen provision på ditt arvode." },
          { q: "Kan jag lämna en recension?", a: "Ja. Efter en bekräftad bokning kan arrangörer lämna ett betyg och en recension, som visas på talangens profil." },
        ],
      },
    },
    invoice: {
      title: "Fakturainställningar",
      lead: "De här uppgifterna används på alla fakturor som skickas från Talentsio. Fyll i det du vill visa längst upp och längst ner på fakturan.",
      groups: {
        logo: "Logga",
        sender: "Avsändare",
        contact: "Kontakt",
        payment: "Betalning",
        terms: "Villkor",
        tax: "Skatt",
        behaviour: "Beteende",
        text: "Text",
      },
      fields: {
        logo: "Liggande logga",
        logoHint: "PNG eller JPG. Cirka 600×180 pixlar ser bäst ut. Utan logga visas ditt artistnamn istället.",
        artistName: "Artistnamn",
        companyName: "Företagsnamn",
        orgNumber: "Organisationsnummer",
        vatNumber: "Momsregistreringsnummer",
        address: "Gatuadress",
        postcode: "Postnummer",
        city: "Ort",
        email: "E-post",
        phone: "Telefon",
        website: "Webbplats",
        bankgiro: "Bankgiro",
        plusgiro: "Plusgiro",
        swish: "Swish",
        iban: "IBAN",
        paymentTermsDays: "Betalvillkor (dagar)",
        lateInterestPct: "Dröjsmålsränta (%)",
        nextInvoiceNumber: "Nästa fakturanummer",
        hasFTax: "Innehar F-skatt",
        vatRegistered: "Momsregistrerad",
        vatRegisteredHint: "När den här är av låses alla momssatser till noll på alla fakturor.",
        autoReminder: "Skicka automatisk påminnelse",
        copyToSelf: "Skicka kopia till mig",
        defaultText: "Standardtext på fakturan",
        referenceText: "Referens",
      },
      preview: {
        heading: "Så här ser din faktura ut",
        invoiceNo: "Fakturanummer",
        invoiceDate: "Fakturadatum",
        dueDate: "Förfallodag",
        billTo: "Faktureras till",
        description: "Beskrivning",
        qty: "Antal",
        unitPrice: "À-pris",
        vat: "Moms",
        amount: "Belopp",
        sampleLine: "Exempeluppdrag",
        subtotal: "Delsumma",
        vatTotal: "Moms",
        total: "Att betala",
        noVat: "Ingen moms",
        payTo: "Betala till",
        talentsioFee: "Talentsios avgift",
        poweredBy: "Powered by Talentsio",
      },
      actions: { save: "Spara", saving: "Sparar", saved: "Sparat", cancel: "Avbryt", upload: "Ladda upp" },
      errors: {
        vatBlocked: "Momsregistrering krävs för momssatser över noll. Aktivera momsregistrering och försök igen.",
        generic: "Det gick inte att spara. Försök igen.",
      },
    },
    addons: {
      title: "Mina tillägg",
      lead: "En lista för allt du säljer utöver din akt. Används sedan i offerter, på din publika profil och på fakturor, så håll den ren.",
      groups: {
        equipment: "Utrustning",
        service: "Tjänster",
      },
      empty: {
        equipment: "Inga tillägg för utrustning än. Lägg till ditt första nedan.",
        service: "Inga tjänste-tillägg än. Lägg till ditt första nedan.",
      },
      badges: {
        showOnProfile: "Syns på profilen",
        allowQuantity: "Antal går att välja",
        onInvoice: "På faktura",
        inactive: "Dold",
        vatLocked: "Moms låst till 0",
      },
      units: {
        piece: "per styck",
        evening: "per kväll",
        hour: "per timme",
        km: "per mil",
        occasion: "per tillfälle",
      },
      vatReasons: {
        exempt_performance: "Framförande undantaget från moms",
        not_registered: "Ej momsregistrerad",
        standard: "Standardsats",
        reduced_admission: "Reducerad (entré / uthyrning)",
        reduced_food: "Reducerad (mat och dryck)",
      },
      form: {
        heading: "Nytt tillägg",
        editHeading: "Redigera tillägg",
        name: "Namn",
        namePlaceholder: "T.ex. Extra speltimme",
        category: "Kategori",
        price: "Pris (SEK)",
        pricePlaceholder: "T.ex. 1500",
        unit: "Enhet",
        vat: "Moms",
        vatReason: "Momsskäl",
        terms: "Villkor",
        termsHint: "Max 300 tecken. Visas för arrangören intill priset.",
        allowQuantity: "Kunden kan välja antal",
        showOnProfile: "Visa på min publika profil",
        usableOnInvoice: "Kan hämtas in på fakturor",
        active: "Aktiv",
      },
      actions: {
        add: "Lägg till tillägg",
        save: "Spara ändringar",
        cancel: "Avbryt",
        edit: "Redigera",
        hide: "Dölj",
        show: "Visa",
        delete: "Ta bort",
        moveUp: "Flytta upp",
        moveDown: "Flytta ner",
      },
      confirm: {
        deleteTitle: "Ta bort detta tillägg?",
        deleteBody: "Raden ligger kvar i din historik så att befintliga offerter fungerar, men nya offerter, profil och fakturor ser den inte längre.",
        deleteCta: "Ja, ta bort",
      },
      toasts: {
        saved: "Tillägg sparat",
        deleted: "Tillägg borttaget",
        reordered: "Ordningen uppdaterad",
      },
      errors: {
        vatBlocked: "Du är inte momsregistrerad, så moms måste vara noll. Aktivera momsregistrering i Fakturainställningar först.",
        generic: "Det gick inte att spara. Försök igen.",
      },
    },
    publicProfile: {
      addons: {
        heading: "Tillägg och extra",
        equipment: "Utrustning",
        service: "Tjänster",
        empty: "Den här talangen har inte lagt in några tillägg än.",
        note: "Priserna nedan är vägledande. Exakta villkor sätts i offerten.",
      },
    },
  },
};
